import { dispatchAwaitableAction, serviceClient, site } from '@yola/ws-sdk';
import store from 'src/js/store';
import reloadSiteMeta from 'src/js/modules/ai-wizard/helpers/reload-site-meta';

let inFlight = null;
let queuedCall = null;

const fullReload = async () => {
  await reloadSiteMeta();
  const activePageId = site.selectors.getActivePageId(store.getState());
  await dispatchAwaitableAction(site.actions.setActivePage(activePageId));
};

const run = async (payload) => {
  if (!payload || !payload.modifications) {
    await fullReload();
    return;
  }

  const { modifications } = payload;

  if (Object.keys(modifications).length === 0) {
    return;
  }

  try {
    const currentSite = site.verifiers.isSiteMetaReloadRequired(modifications)
      ? (await serviceClient.get().getCurrentSite()).data
      : undefined;
    await dispatchAwaitableAction(site.actions.applySiteMetaChanges(modifications, currentSite));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Partial apply of AI chat changes failed, falling back to full reload', error);
    await fullReload();
  }
};

const hasModifications = (payload) => Boolean(payload && payload.modifications);

const mergeModifications = (current, next) => {
  const merged = { ...current };

  Object.keys(next).forEach((key) => {
    if (!Array.isArray(next[key])) return;

    const currentValues = Array.isArray(merged[key]) ? merged[key] : [];
    const nextValues = Array.isArray(next[key]) ? next[key] : [];
    merged[key] = [...new Set([...currentValues, ...nextValues])];
  });

  return merged;
};

// A call without modifications is a full reload and replaces any queued partial apply.
// An empty modifications object adds no ids.
const queuePayload = (payload) => {
  if (!queuedCall) {
    queuedCall = { payload };
    return;
  }

  if (!hasModifications(queuedCall.payload) || !hasModifications(payload)) {
    queuedCall = { payload: undefined };
    return;
  }

  queuedCall = {
    payload: {
      modifications: mergeModifications(queuedCall.payload.modifications, payload.modifications),
    },
  };
};

const runLatest = (payload) => {
  queuedCall = null;

  return run(payload).then(() => {
    if (!queuedCall) return undefined;

    const { payload: nextPayload } = queuedCall;
    return runLatest(nextPayload);
  });
};

/**
 * Applies the chat's site changes. Calls made while one is running are merged
 * into one trailing apply.
 *
 * Without a payload, or without `modifications`, the whole site is reloaded.
 * An empty `modifications` object does nothing. Every modifications value is a
 * list of string ids.
 *
 * @param {Object} [payload]
 * @param {Object} [payload.modifications]
 * @param {string[]} [payload.modifications.page_content_modified] Ids of pages whose HTML changed.
 * @param {string[]} [payload.modifications.page_meta_modified] Ids of pages whose meta changed.
 * @param {string[]} [payload.modifications.pages_created] Ids of pages the chat created.
 * @param {string[]} [payload.modifications.globals_modified] Ids of globals whose HTML changed.
 * @param {string[]} [payload.modifications.site_settings_modified] Names of the site settings that changed.
 * @returns {Promise<void>}
 */
const applyChatChanges = (payload) => {
  if (inFlight) {
    queuePayload(payload);
    return inFlight;
  }

  inFlight = runLatest(payload).finally(() => {
    inFlight = null;
    queuedCall = null;
  });

  return inFlight;
};

export default applyChatChanges;
